import { contrastInk, hexFromOklch, oklchFromHex } from './color'
import type { TSchmancyTheme } from './theme.interface'
import {
	ACHROMATIC_IDENTITY_HUE,
	CHROMA,
	DARK,
	FIXED,
	HUE_ROTATION,
	LIGHT,
	MODE_COLORS,
	REFERENCE_LIGHTNESS,
	themeConstants,
} from './theme.policy'

function brandColors(
	C: number,
	hue: number,
	recipe: typeof LIGHT,
	grounds: readonly string[],
	lightness = recipe.role,
) {
	const foreground = contrastInk(lightness, C, hue, grounds, 4.5, recipe === LIGHT)
	const container = hexFromOklch(recipe.container, C, hue)
	const fixed = hexFromOklch(FIXED.fixed, C, hue)
	const fixedDim = hexFromOklch(FIXED.fixedDim, C, hue)
	return {
		default: foreground,
		on: contrastInk(recipe.roleOn, C, hue, [foreground], 4.5, recipe === DARK),
		container,
		onContainer: contrastInk(recipe.onContainer, C, hue, [container], 4.5, recipe === LIGHT),
		fixed,
		fixedDim,
		onFixed: contrastInk(FIXED.onFixed, C, hue, [fixed, fixedDim], 4.5, true),
		onFixedVariant: contrastInk(FIXED.onFixedVariant, C, hue, [fixed, fixedDim], 4.5, true),
	}
}

function stateColors(profile: TSchmancyTheme['sys']['color']['error'], grounds: readonly string[], isDark: boolean) {
	const foreground = contrastInk(...oklchFromHex(profile.default), grounds, 4.5, !isDark)
	return { ...profile, default: foreground, on: contrastInk(...oklchFromHex(profile.on), [foreground], 4.5, isDark) }
}

export function formatTheme(seed: string, isDark = false): Omit<TSchmancyTheme, 'locale'> {
	const [lightness, chroma, hue] = oklchFromHex(seed)
	const recipe = isDark ? DARK : LIGHT
	const neutralChroma = Math.min(CHROMA.neutral.limit, chroma * CHROMA.neutral.scale)
	const variantChroma = Math.min(CHROMA.neutralVariant.limit, chroma * CHROMA.neutralVariant.scale)
	const secondaryChroma = Math.min(CHROMA.secondary.limit, chroma * CHROMA.secondary.scale)
	const tertiaryChroma = Math.min(CHROMA.tertiary.limit, chroma * CHROMA.tertiary.scale)
	const [background, dim, bright, containerLowest, containerLow, container, containerHigh, containerHighest] =
		recipe.surface.map(lightness => hexFromOklch(lightness, neutralChroma, hue))
	const grounds = [background, dim, bright, containerLowest, containerLow, container, containerHigh, containerHighest]
	const primary = brandColors(chroma, hue, recipe, grounds, lightness)
	const inverse = hexFromOklch(recipe.inverse, neutralChroma, hue)
	const colors = MODE_COLORS[isDark ? 'dark' : 'light']
	const referenceMemo = new Map<string, string>()
	const referencePalette = (C: number, h: number): TSchmancyTheme['ref']['palette']['primary'] => {
		const shade = (L: number) => {
			const key = `${L}/${C}/${C === 0 ? 0 : h}`
			const cached = referenceMemo.get(key)
			if (cached !== undefined) return cached
			const value = hexFromOklch(L, C, h)
			referenceMemo.set(key, value)
			return value
		}
		return {
			0: shade(REFERENCE_LIGHTNESS[0]),
			10: shade(REFERENCE_LIGHTNESS[10]),
			20: shade(REFERENCE_LIGHTNESS[20]),
			30: shade(REFERENCE_LIGHTNESS[30]),
			40: shade(REFERENCE_LIGHTNESS[40]),
			50: shade(REFERENCE_LIGHTNESS[50]),
			60: shade(REFERENCE_LIGHTNESS[60]),
			70: shade(REFERENCE_LIGHTNESS[70]),
			80: shade(REFERENCE_LIGHTNESS[80]),
			90: shade(REFERENCE_LIGHTNESS[90]),
			95: shade(REFERENCE_LIGHTNESS[95]),
			99: shade(REFERENCE_LIGHTNESS[99]),
			100: shade(REFERENCE_LIGHTNESS[100]),
		}
	}
	const identityTint = (index: number) => {
		const h = (chroma === 0 ? ACHROMATIC_IDENTITY_HUE : hue) + index * HUE_ROTATION
		const tint = hexFromOklch(recipe.identity[0], recipe.identity[1], h)
		return {
			container: tint,
			onContainer: contrastInk(recipe.onContainer, recipe.identity[1], h, [tint], 4.5, !isDark),
		}
	}
	const [, errorChroma, errorHue] = oklchFromHex(MODE_COLORS.light.error.pinned)
	return {
		sys: {
			color: {
				scrim: '#000000',
				shadow: '#000000',
				outline: contrastInk(recipe.outline, variantChroma, hue, grounds, 3, !isDark),
				outlineVariant: hexFromOklch(recipe.outlineVariant, variantChroma, hue),
				surface: {
					default: background,
					dim,
					bright,
					containerLowest,
					containerLow,
					container,
					containerHigh,
					containerHighest,
					on: contrastInk(recipe.on, neutralChroma, hue, grounds, 4.5, !isDark),
					onVariant: contrastInk(recipe.onVariant, variantChroma, hue, grounds, 4.5, !isDark),
					inverse,
					inverseOn: contrastInk(recipe.inverseOn, neutralChroma, hue, [inverse], 4.5, isDark),
					tint: primary.default,
					low: containerLow,
					high: containerHigh,
					highest: containerHighest,
					lowest: containerLowest,
				},
				primary: {
					...primary,
					inverse: contrastInk(lightness, chroma, hue, [inverse], 4.5, isDark),
				},
				secondary: brandColors(secondaryChroma, hue, recipe, grounds),
				tertiary: brandColors(tertiaryChroma, hue + HUE_ROTATION, recipe, grounds),
				error: stateColors(colors.error, grounds, isDark),
				success: stateColors(colors.success, grounds, isDark),
				warning: stateColors(colors.warning, grounds, isDark),
				info: stateColors(colors.info, grounds, isDark),
				identity: {
					0: identityTint(0),
					1: identityTint(1),
					2: identityTint(2),
					3: identityTint(3),
					4: identityTint(4),
					5: identityTint(5),
				},
				series: {
					0: { ...colors.series[0] },
					1: { ...colors.series[1] },
					2: { ...colors.series[2] },
					3: { ...colors.series[3] },
					4: { ...colors.series[4] },
					5: { ...colors.series[5] },
				},
			},
			...themeConstants(isDark),
		},
		ref: {
			palette: {
				primary: referencePalette(chroma, hue),
				secondary: referencePalette(secondaryChroma, hue),
				tertiary: referencePalette(tertiaryChroma, hue + HUE_ROTATION),
				neutral: referencePalette(neutralChroma, hue),
				neutralVariant: referencePalette(variantChroma, hue),
				error: referencePalette(errorChroma, errorHue),
			},
		},
	}
}
